/**
 * AddChildButton - Dropdown button for adding attachments or sub-items
 *
 * Used in Column 3 header of ItemEditor.
 * Shows: Attachments (paperclip icon), divider, then sub-item types
 */

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Paperclip,
  CheckSquare,
  DollarSign,
  FileText,
  Calendar,
  ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui';
import type { ItemType } from '@perfin/shared';

// ============================================================================
// TYPES
// ============================================================================

interface AddChildButtonProps {
  onAddAttachment: () => void;
  onAddSubItem?: (type: ItemType) => void;
  allowedTypes?: ItemType[];
  disabled?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TYPE_ICONS: Record<ItemType, React.ElementType> = {
  task: CheckSquare,
  transaction: DollarSign,
  note: FileText,
  keyDate: Calendar,
};

const TYPE_LABELS: Record<ItemType, string> = {
  task: 'Task',
  transaction: 'Transaction',
  note: 'Note',
  keyDate: 'Key Date',
};

const TYPE_COLORS: Record<ItemType, string> = {
  task: 'text-blue-600',
  transaction: 'text-green-600',
  note: 'text-amber-600',
  keyDate: 'text-purple-600',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function AddChildButton({
  onAddAttachment,
  onAddSubItem,
  allowedTypes = ['task', 'keyDate', 'transaction', 'note'],
  disabled = false,
}: AddChildButtonProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position when expanded
  useEffect(() => {
    if (isExpanded && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.right - 180, // Right-align dropdown
      });
    }
  }, [isExpanded]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isExpanded) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const clickedButton = buttonRef.current?.contains(target);
      const clickedDropdown = dropdownRef.current?.contains(target);
      if (!clickedButton && !clickedDropdown) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded]);

  return (
    <>
      <Button
        ref={buttonRef}
        variant="primary"
        size="sm"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        disabled={disabled}
      >
        Add
        <ChevronDown className="h-3 w-3 ml-1" />
      </Button>

      {isExpanded && createPortal(
        <div
          ref={dropdownRef}
          className="fixed bg-white border rounded-lg shadow-lg z-50 py-1 min-w-[180px]"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Attachments option */}
          <button
            onClick={() => {
              onAddAttachment();
              setIsExpanded(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 text-left"
          >
            <Paperclip className="h-4 w-4" />
            Attachment
          </button>

          {/* Sub-item types - only show when handler is provided */}
          {onAddSubItem && (
            <>
              {/* Divider */}
              <div className="h-px bg-gray-200 my-1" />

              {allowedTypes.map((type) => {
                const Icon = TYPE_ICONS[type];
                return (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAddSubItem(type);
                      setIsExpanded(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 text-left"
                  >
                    <Icon className={`h-4 w-4 ${TYPE_COLORS[type]}`} />
                    {TYPE_LABELS[type]}
                  </button>
                );
              })}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
